import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectTeamId } from '@/containers/Login/AuthSelectors'
import { updateCutOffers } from '@/containers/Login/AuthSlice'

/**
 * Subscribes to the team-scoped Echo channel that broadcasts the current list
 * of offers in a cut period for the logged-in agent. Each event carries the
 * fresh, complete list — we replace the slice state with what we receive.
 *
 *   Channel : team.offers-in-cut-period.{teamId}
 *
 * We listen to ALL events on the channel (rather than pinning a specific
 * event name) so the front-end is resilient to the broadcast event name
 * being adjusted server-side. We also try multiple known payload shapes
 * before giving up so changes in the API contract don't silently break the
 * banner refresh.
 */

// Try to find the cut-offers array in the broadcast payload regardless of the
// wrapping shape. Returns the array or `null` if nothing usable is found.
const extractCutOffers = (event) => {
  if (!event) return null
  if (Array.isArray(event)) return event
  if (Array.isArray(event.cut_offers)) return event.cut_offers
  if (Array.isArray(event.cutOffers)) return event.cutOffers
  if (Array.isArray(event.offers)) return event.offers
  if (Array.isArray(event.periods)) return event.periods
  if (event.data) {
    if (Array.isArray(event.data)) return event.data
    if (Array.isArray(event.data.cut_offers)) return event.data.cut_offers
    if (Array.isArray(event.data.cutOffers)) return event.data.cutOffers
    if (Array.isArray(event.data.offers)) return event.data.offers
    if (Array.isArray(event.data.periods)) return event.data.periods
  }
  if (event.message) {
    if (Array.isArray(event.message)) return event.message
    if (Array.isArray(event.message.cut_offers)) return event.message.cut_offers
    if (Array.isArray(event.message.offers)) return event.message.offers
  }
  return null
}

const useCutOffersListener = () => {
  const dispatch = useDispatch()
  // Use the live-from-Redux selector. `selectAuthData` falls back to a stale
  // localStorage snapshot which would freeze the teamId across sessions.
  const teamId = useSelector(selectTeamId)

  useEffect(() => {
    if (!teamId) return undefined
    if (typeof window === 'undefined' || !window.Echo) return undefined

    const channelName = `team.offers-in-cut-period.${teamId}`
    const channel = window.Echo.channel(channelName)

    const handleEvent = (eventName, rawEvent) => {
      // Log in dev so we can pinpoint the actual event name + payload shape
      // if the dispatch ever fails to fire as expected.
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.log('[cutOffers] broadcast received', { channel: channelName, eventName, rawEvent })
      }

      // Two shapes observed in the wild:
      //   1) An already-parsed object (normal Laravel Echo .listen callback).
      //   2) A wrapper { data: "<JSON string>" } from listenToAll on top of
      //      pusher-js, where the `data` field hasn't been auto-parsed yet.
      // Normalize to a parsed object before searching for the offers array.
      let event = rawEvent
      if (rawEvent && typeof rawEvent === 'object' && typeof rawEvent.data === 'string') {
        try {
          event = JSON.parse(rawEvent.data)
        } catch {
          // Fall through to use the raw payload as-is.
        }
      } else if (typeof rawEvent === 'string') {
        try {
          event = JSON.parse(rawEvent)
        } catch {
          /* keep rawEvent */
        }
      }

      const next = extractCutOffers(event)
      if (Array.isArray(next)) {
        dispatch(updateCutOffers(next))
      }
    }

    // Prefer `listenToAll` (catches whatever event name the backend emits).
    // Fall back to the project's `.operation.success` convention when the
    // helper isn't available on the channel implementation we got.
    if (typeof channel.listenToAll === 'function') {
      channel.listenToAll((eventName, event) => handleEvent(eventName, event))
    } else {
      channel.listen('.operation.success', (event) => handleEvent('.operation.success', event))
    }

    return () => {
      // Stop listening to avoid stale callbacks after logout / team change.
      if (typeof channel.stopListening === 'function') {
        channel.stopListening('.operation.success')
      }
      window.Echo.leaveChannel(channelName)
    }
  }, [teamId, dispatch])
}

export default useCutOffersListener
